import { isCancel, type AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { matchPath } from "react-router-dom";
import { EXPECTED_FLOW_SHORT_MESSAGES } from "./shortMessages";

// Nomes estáveis de operação evitam enviar CPF, telefone, tokens e IDs presentes nas URLs.
const operations: ReadonlyArray<readonly [method: string, path: string, name: string]> = [
  ["POST", "/core/vendas", "criar_venda"],
  ["PUT", "/core/vendas/trocar-pagamento", "trocar_pagamento"],
  ["POST", "/core/elegibilidade", "elegibilidade"],
  ["POST", "/core/elegibilidade/express", "elegibilidade_express"],
  ["GET", "/core/elegibilidade/analise/:id", "analise_seguranca"],
  ["POST", "/core/sms-confirmacao/enviar-sms", "enviar_sms"],
  ["POST", "/core/sms-confirmacao/confirmar-recebimento", "confirmar_sms"],
  ["POST", "/core/vendas/agendar-ativacao/:id", "agendar_ativacao"],
  ["POST", "/core/vendas/non-auth/ativar", "solicitar_ativacao"],
  ["POST", "/core/vendas/iniciar-adesao-m4u", "iniciar_adesao_m4u"],
  ["POST", "/m4u/v1/tokenizar-cartao", "tokenizar_cartao"],
  ["PUT", "/core/vendas/tokenizar-cartao", "salvar_cartao_tokenizado"],
  ["POST", "/core/vendas/oferta-express", "oferta_express"],
  ["POST", "/integrations/dados-pessoais/buscar-endereco-valido", "buscar_endereco"],
  ["GET", "/integrations/dados-pessoais/:cpf", "consultar_dados_pessoais"],
  ["GET", "/core/enderecos/:cep", "consultar_cep"],
];

type ErrorCategory = "timeout" | "rede" | "regra_negocio" | "servidor" | "validacao";

export type CheckoutEvent = {
  operacao: string;
  resultado: "sucesso" | "erro" | "pendente";
  http_status: number | null;
  codigo?: string;
  categoria?: ErrorCategory;
};

type RecordCheckoutEvent = (event: CheckoutEvent) => void;

function getOperation(config?: AxiosRequestConfig): string | undefined {
  if (!config?.url) return;
  const path = new URL(config.url, "https://checkout.invalid").pathname;
  const method = config.method?.toUpperCase();
  return operations.find(([verb, route]) =>
    verb === method && matchPath({ path: route, end: true }, path)
  )?.[2];
}

function getApiErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== "object" || !("shortMessage" in data)) return;
  const code = data.shortMessage;
  // shortMessage é um identificador da API. Mensagens livres ficam fora da telemetria.
  const identifierCharacters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_-";
  if (
    typeof code === "string" && code.length > 0 && code.length <= 80 &&
    Array.from(code).every((character) => identifierCharacters.includes(character))
  ) {
    return code;
  }
}

function getTransportErrorCode(status: number | null, isTimeout: boolean): string {
  if (isTimeout) return "TIMEOUT";
  if (status === null) return "SEM_CONEXAO";
  return `HTTP_${status}`;
}

function getErrorCategory(status: number | null, code: string, isTimeout: boolean): ErrorCategory {
  if (isTimeout) return "timeout";
  if (status === null) return "rede";
  const expectedCodes: ReadonlySet<string> = EXPECTED_FLOW_SHORT_MESSAGES;
  if (expectedCodes.has(code)) return "regra_negocio";
  return status >= 500 ? "servidor" : "validacao";
}

export function recordCheckoutSuccess(response: AxiosResponse<unknown>, record: RecordCheckoutEvent) {
  try {
    const operacao = getOperation(response.config);
    if (!operacao) return;
    if (operacao === "criar_venda" && response.status === 202) {
      // SaleController::store retorna 202 quando a criação falha e vai para correção.
      record({ operacao, resultado: "pendente", http_status: 202, codigo: "VENDA_EM_CORRECAO" });
      return;
    }
    record({ operacao, resultado: "sucesso", http_status: response.status });
  } catch {
    // A telemetria não pode substituir a resposta original da API por uma falha própria.
    console.warn("[Checkout] Não foi possível registrar o resultado.");
  }
}

export function recordCheckoutError(error: AxiosError<unknown>, record: RecordCheckoutEvent) {
  if (isCancel(error)) return;
  try {
    const operacao = getOperation(error.config);
    if (!operacao) return;
    const status = error.response?.status ?? null;
    const isTimeout = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";
    const codigo = getApiErrorCode(error.response?.data) ?? getTransportErrorCode(status, isTimeout);
    const categoria = getErrorCategory(status, codigo, isTimeout);
    record({ operacao, resultado: "erro", http_status: status, codigo, categoria });
  } catch {
    console.warn("[Checkout] Não foi possível registrar o erro.");
  }
}
